import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { get } from 'lodash';
import { setCommissionDeviceGain, doClearApplyError, setCommissionGa2LastSu } from 'redux/Commissioning';
import CommissionErrorBox from 'components/features/Commissioning/CommissionErrorBox/CommissionErrorBox';
import { ProgressIndicator } from 'components/shared/Progress';
import { useSyncFormStep } from '../../hooks';
import Button from 'components/shared/Button';
import Form from 'components/shared/Form';
import FormTable from 'components/shared/FormTable';
import styles from './InServiceForm.module.css';

const SET_LASTSU_ERROR_MESSAGE = 'Last SU should only be on of the following values: 1, 2, 3, or 4';

const parameters = [
  {
    name: 'gainMode',
    label: 'Gain Mode',
    disabled: true
  },
  {
    name: 'lastSU',
    label: 'Last SU',
    option: {
      required: 'Last SU is required',
      pattern: {
        value: /^[1-4]$/, // [1, 2, 3, 4]
        message: 'Last SU should only be on of the following values: 1, 2, 3, or 4' //ToDo: make message available from formState
      }
    }
  }
];

const defaultValues = {};

function InServiceForm({ value }) {
  const dispatch = useDispatch();
  const valuesRef = useRef(defaultValues);
  const deviceData = useSelector((state) => state.data.commissioningGA2.deviceData);
  const lastSU = useSelector((state) => state.data.commissioningGA2.lastSu);
  const deviceProperties = deviceData?.properties?.reported;
  const applyError = useSelector((state) => state.data.commissioningGA2.applyError);
  const methods = useForm({ defaultValues: lastSU ? { lastSU: lastSU } : defaultValues });
  const { formState, reset } = methods;
  const { isSubmitting, isValid, isDirty } = formState;
  const values = useMemo(
    () => ({
      gainMode: get(deviceProperties, 'DU.gain-settings.configure-mode', ''),
      lastSU: get(deviceProperties, 'DU.gain-settings.last-su', '')
    }),
    [deviceProperties]
  );
  const meta = useMemo(
    () => ({
      gainMode: {
        lastUpdated: get(deviceProperties, '$metadata.DU.gain-settings.configure-mode.$lastUpdated')
      },
      lastSU: {
        lastUpdated: get(deviceProperties, '$metadata.DU.gain-settings.last-su.$lastUpdated')
      }
    }),
    [deviceProperties]
  );

  const handleSubmit = useCallback((values) => {
    valuesRef.current = values

    return dispatch(setCommissionGa2LastSu(parseInt(values.lastSU)));
  }, [dispatch]);

  const handleCloseErrorBox = useCallback(() => {
    reset();
    dispatch(doClearApplyError());
  }, [dispatch, reset]);

  const handleReset = useCallback(
    (event) => {
      event.preventDefault();
      reset();
    },
    [reset]
  );

  useSyncFormStep(value, formState);
  useEffect(() => {
    if (formState.isSubmitSuccessful) {
      reset(valuesRef.current);
    }
  }, [formState, reset]);

  return (
    <Form methods={methods} onSubmit={handleSubmit} className={styles.root}>
      {applyError && (
        <CommissionErrorBox
          title={`Set Last SU Error: ${applyError.type}`}
          message={applyError.message}
          onClose={handleCloseErrorBox}
        />
      )}
      {isSubmitting && (
        <div className={styles.progress}>
          <ProgressIndicator />
        </div>
      )}
      <div className={styles.control}>
        <FormTable values={values} parameters={parameters} meta={meta} />
        <div className={styles.actions}>
          <div className={styles.error}>{!isValid && SET_LASTSU_ERROR_MESSAGE}</div>
          <Button type="reset" variant="link" size="medium" color="primary" onClick={handleReset}>
            Clear
          </Button>

          <div title={null}>
            <Button
              type="submit"
              variant="contained"
              size="medium"
              color="primary"
              disabled={isSubmitting || !isValid || !isDirty}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </Form>
  );
}

export default InServiceForm;
